<template>
  <div class="">
    <main class="main">
      <section class="section">
        <div class="container container--medium">

          <div class="season-start__hero">
            <h1 class="season-start__title">{{ seasonInfo.title }}</h1>
            <p class="season-start__period">
              <i class="fa-regular fa-calendar"></i>
              {{ seasonInfo.startDate }} — {{ seasonInfo.endDate }}
              <span class="season-start__period-divider">·</span>
              Диплом участника при&nbsp;выполнении ≥{{ seasonInfo.diplomaThresholdPercent }}% цели
            </p>
            <p class="season-start__lead">
              Командный проект ФНС&nbsp;ЛАБ спорт по&nbsp;накоплению личной дистанции.
              Укажите цель в&nbsp;километрах в&nbsp;каждой активности, в&nbsp;которой хотите
              участвовать,&nbsp;— по&nbsp;цели вы&nbsp;попадёте в&nbsp;один из&nbsp;пяти
              кластеров. Можно выбрать одну активность или несколько сразу.
            </p>
          </div>

          <div class="season-start__scale">
            <p class="season-start__scale-title">Кластеры сезона по&nbsp;дистанции</p>
            <div class="season-start__scale-items">
              <div
                v-for="c in seasonInfo.clusters"
                :key="c.id"
                class="season-start__scale-item"
                :class="{ 'season-start__scale-item--active': activeClusterIds.includes(c.id) }"
              >
                <span class="season-start__scale-num">{{ c.id }}</span>
                <span class="season-start__scale-range">{{ c.label }}</span>
              </div>
            </div>
          </div>

          <div class="season-start__form">
            <div class="season-start__form-head">
              <h2 class="season-start__form-title">Мои цели на&nbsp;сезон</h2>
              <p class="season-start__form-subtitle">
                Минимум 50&nbsp;км в&nbsp;активности. Пустые поля&nbsp;— активность
                в&nbsp;сезоне не&nbsp;учитывается.
              </p>
            </div>
            <ul class="season-start__activities">
              <li
                v-for="row in form"
                :key="row.code"
                class="season-start__activity"
                :class="{
                  'season-start__activity--filled': !!rowCluster(row),
                  'season-start__activity--invalid': isRowInvalid(row),
                }"
              >
                <label :for="'goal-' + row.code" class="season-start__activity-name">
                  <span class="season-start__activity-icon">
                    <i class="fa-solid" :class="row.icon"></i>
                  </span>
                  {{ row.name }}
                </label>
                <div class="season-start__activity-input-wrap">
                  <input
                    :id="'goal-' + row.code"
                    type="number"
                    inputmode="numeric"
                    class="season-start__activity-input"
                    v-model.number="row.goalKm"
                    placeholder="—"
                    min="50"
                    :disabled="seasonLoading.progress"
                  />
                  <span class="season-start__activity-unit">км</span>
                </div>
                <div class="season-start__activity-result">
                  <template v-if="rowCluster(row)">
                    <span class="season-start__pill season-start__pill--ok">
                      Кластер&nbsp;{{ rowCluster(row).id }}
                    </span>
                    <span class="season-start__activity-range">{{ rowCluster(row).label }}</span>
                  </template>
                  <span
                    v-else-if="isRowInvalid(row)"
                    class="season-start__pill season-start__pill--err"
                  >
                    Минимум 50&nbsp;км
                  </span>
                  <span v-else class="season-start__activity-empty">не&nbsp;участвую</span>
                </div>
              </li>
            </ul>
          </div>

          <div class="season-start__footer">
            <a
              href="javascript:void(0)"
              class="button button-accent-1 season-start__submit"
              :class="{ 'season-start__submit--disabled': !canSave }"
              @click="confirmSave"
            >
              {{
                seasonLoading.save
                  ? 'Сохраняем...'
                  : isGoalSet
                    ? 'Сохранить'
                    : 'Сохранить и начать сезон'
              }}
            </a>
            <p v-if="hasAnyInvalid" class="season-start__foot-hint season-start__foot-hint--err">
              В&nbsp;одной из&nbsp;активностей цель меньше 50&nbsp;км&nbsp;— поправьте, чтобы продолжить.
            </p>
            <p v-else-if="!canSave && !seasonLoading.save" class="season-start__foot-hint">
              Чтобы сохранить, заполните хотя&nbsp;бы одну активность.
            </p>
          </div>

        </div>
      </section>
    </main>
    <v-dialog />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import errorHandlerForResponse from '@/helpers/errorHandlerForResponse.js'

export default {
  name: 'SeasonStartPage',

  data() {
    return {
      form: [],
    }
  },

  computed: {
    ...mapGetters('season', ['activities', 'seasonInfo', 'loading', 'isGoalSet']),
    seasonLoading() {
      return this.loading
    },
    filledRows() {
      return this.form.filter(r => this.rowCluster(r))
    },
    rowsToSave() {
      return this.form.filter(r => {
        if (this.rowCluster(r)) return true
        if (this.hasValue(r) && Number(r.goalKm) === 0) return true
        return false
      })
    },
    activeClusterIds() {
      const ids = this.filledRows.map(r => this.rowCluster(r).id)
      return Array.from(new Set(ids))
    },
    hasAnyInvalid() {
      return this.form.some(r => this.isRowInvalid(r))
    },
    canSave() {
      if (this.seasonLoading.progress || this.seasonLoading.save) return false
      if (this.hasAnyInvalid) return false
      return this.rowsToSave.length > 0
    },
  },

  watch: {
    activities() {
      if (this.seasonLoading.progress || this.isGoalSet || this.form.length === 0) {
        this.syncFormFromActivities()
      }
    },
  },

  created() {
    this.syncFormFromActivities()
  },

  methods: {
    syncFormFromActivities() {
      const source = this.activities && this.activities.length
        ? this.activities
        : (this.seasonInfo.activityCatalog || [])
      this.form = source.map(a => ({
        code: a.code,
        activityType: a.activityType || a.code,
        name: a.name,
        icon: a.icon,
        goalKm: a.goalKm,
      }))
    },
    hasValue(row) {
      return row.goalKm !== null && row.goalKm !== '' && !Number.isNaN(row.goalKm)
    },
    rowCluster(row) {
      if (!this.hasValue(row)) return null
      const km = Number(row.goalKm)
      if (!Number.isInteger(km) || km < 50) return null
      return this.seasonInfo.clusters.find(c => km >= c.min && km <= c.max) || null
    },
    isRowInvalid(row) {
      if (!this.hasValue(row)) return false
      const km = Number(row.goalKm)
      if (km === 0) return false
      return !this.rowCluster(row)
    },
    async confirmSave() {
      if (!this.canSave) return
      const payload = this.rowsToSave.map(r => ({
        code: r.code,
        activityType: r.activityType || r.code,
        goalKm: Number(r.goalKm),
      }))
      if (this.isGoalSet) {
        try {
          await this.$store.dispatch('season/saveGoals', { activities: payload })
          this.$router.push('/season/saved')
        } catch (e) {
          this.errorHandlerForResponse(e)
        }
        return
      }
      this.$modal.show('dialog', {
        text: 'Сохранить цели на сезон?',
        buttons: [
          {
            title: 'Отмена',
            handler: () => {
              this.$modal.hide('dialog')
            },
          },
          {
            title: 'Сохранить',
            handler: async () => {
              this.$modal.hide('dialog')
              try {
                await this.$store.dispatch('season/saveGoals', { activities: payload })
                this.$router.push('/season/saved')
              } catch (e) {
                this.errorHandlerForResponse(e)
              }
            },
          },
        ],
      })
    },
    errorHandlerForResponse,
  },
}
</script>

<style>
/* Hero */
.season-start__hero {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 28px;
}

.season-start__title {
  font-size: 26px;
  font-weight: 700;
  color: #012653;
  margin: 0 0 10px;
  line-height: 1.25;
}

.season-start__period {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7a8f;
  margin: 0 0 18px;
}

.season-start__period i {
  font-size: 13px;
  opacity: 0.8;
}

.season-start__period-divider {
  opacity: 0.5;
}

.season-start__lead {
  font-size: 15px;
  line-height: 1.6;
  color: #333;
  margin: 0;
}

/* Cluster scale */
.season-start__scale {
  background: #f4f7fb;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 24px;
}

.season-start__scale-title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6b7a8f;
  margin: 0 0 12px;
}

.season-start__scale-items {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.season-start__scale-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px 6px 6px;
  background: #fff;
  border: 1.5px solid #d9e2ec;
  border-radius: 999px;
  font-size: 13px;
  color: #012653;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}

.season-start__scale-item--active {
  background: #012653;
  border-color: #012653;
  color: #fff;
}

.season-start__scale-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #EDF3FA;
  color: #012653;
  font-weight: 700;
  font-size: 11px;
  flex-shrink: 0;
}

.season-start__scale-item--active .season-start__scale-num {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
}

.season-start__scale-range {
  white-space: nowrap;
}

/* Form */
.season-start__form {
  background: #fff;
  border: 1.5px solid #EDF3FA;
  border-radius: 14px;
  margin-bottom: 24px;
  overflow: hidden;
}

.season-start__form-head {
  padding: 18px 24px 14px;
  border-bottom: 1px solid #EDF3FA;
}

.season-start__form-title {
  font-size: 17px;
  font-weight: 700;
  color: #012653;
  margin: 0 0 4px;
}

.season-start__form-subtitle {
  font-size: 13px;
  color: #6b7a8f;
  margin: 0;
}

.season-start__activities {
  list-style: none;
  padding: 0;
  margin: 0;
}

.season-start__activity {
  display: grid;
  grid-template-columns: minmax(160px, 1fr) 160px minmax(200px, 1.2fr);
  gap: 20px;
  align-items: center;
  padding: 14px 24px;
  border-top: 1px solid #EDF3FA;
  transition: background 0.15s;
}

.season-start__activity:first-child {
  border-top: none;
}

.season-start__activity--filled {
  background: #f7fbf9;
}

.season-start__activity--invalid {
  background: #fef6f5;
}

.season-start__activity-name {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  font-size: 15px;
  font-weight: 600;
  color: #012653;
  cursor: pointer;
  user-select: none;
}

.season-start__activity-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #EDF3FA;
  color: #012653;
  font-size: 16px;
  flex-shrink: 0;
  transition: background 0.15s, color 0.15s;
}

.season-start__activity--filled .season-start__activity-icon {
  background: #012653;
  color: #fff;
}

.season-start__activity-input-wrap {
  position: relative;
}

.season-start__activity-input {
  width: 100%;
  height: 44px;
  padding: 0 38px 0 14px;
  border: 1.5px solid #b0bfcf;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #012653;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
  -moz-appearance: textfield;
  appearance: textfield;
}

.season-start__activity-input::-webkit-outer-spin-button,
.season-start__activity-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.season-start__activity-input::placeholder {
  color: #b0bfcf;
  font-weight: 400;
}

.season-start__activity-input:focus {
  outline: none;
  border-color: #012653;
  box-shadow: 0 0 0 3px rgba(1, 38, 83, 0.12);
}

.season-start__activity-input:disabled {
  background: #f4f7fb;
  color: #6b7a8f;
}

.season-start__activity--invalid .season-start__activity-input {
  border-color: #c0392b;
}

.season-start__activity--invalid .season-start__activity-input:focus {
  box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.12);
}

.season-start__activity-unit {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
  font-weight: 500;
  color: #6b7a8f;
  pointer-events: none;
}

.season-start__activity-result {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  min-height: 24px;
}

.season-start__pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.season-start__pill--ok {
  background: #e6f4ec;
  color: #1a7a4a;
}

.season-start__pill--err {
  background: #fdecea;
  color: #c0392b;
}

.season-start__activity-range {
  color: #6b7a8f;
  font-size: 13px;
  white-space: nowrap;
}

.season-start__activity-empty {
  color: #b0bfcf;
  font-style: italic;
  font-size: 13px;
}

/* Footer */
.season-start__footer {
  text-align: center;
}

.season-start__warn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff7e3;
  color: #5a3b00;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 13px;
  margin: 0 0 18px;
}

.season-start__warn i {
  color: #c9881a;
}

.season-start__submit {
  min-width: 280px;
}

.season-start__submit--disabled {
  opacity: 0.45;
  pointer-events: none;
}

.season-start__foot-hint {
  margin: 14px 0 0;
  font-size: 13px;
  color: #6b7a8f;
}

.season-start__foot-hint--err {
  color: #c0392b;
}

/* Mobile */
@media screen and (max-width: 760px) {
  .season-start__hero {
    margin-bottom: 22px;
  }

  .season-start__title {
    font-size: 22px;
    line-height: 1.3;
  }

  .season-start__period {
    font-size: 13px;
    margin-bottom: 14px;
  }

  .season-start__period-divider {
    display: none;
  }

  .season-start__period > * {
    flex-basis: auto;
  }

  .season-start__lead {
    font-size: 14px;
    text-align: left;
  }

  .season-start__scale {
    padding: 14px 16px;
    margin-bottom: 18px;
  }

  .season-start__scale-title {
    margin-bottom: 10px;
  }

  .season-start__scale-items {
    gap: 6px;
  }

  .season-start__scale-item {
    padding: 4px 10px 4px 4px;
    font-size: 12px;
  }

  .season-start__scale-num {
    width: 20px;
    height: 20px;
    font-size: 10px;
  }

  .season-start__form {
    border-radius: 12px;
    margin-bottom: 18px;
  }

  .season-start__form-head {
    padding: 14px 16px 12px;
  }

  .season-start__form-title {
    font-size: 16px;
  }

  .season-start__activity {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 14px 16px;
  }

  .season-start__activity-name {
    gap: 10px;
    font-size: 14px;
  }

  .season-start__activity-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
    border-radius: 8px;
  }

  .season-start__activity-input-wrap {
    width: 200px;
    max-width: 100%;
  }

  .season-start__activity-input {
    height: 42px;
    font-size: 16px; /* prevents iOS zoom on focus */
  }

  .season-start__activity-result {
    min-height: 0;
    flex-wrap: wrap;
    gap: 8px;
  }

  .season-start__warn {
    width: 100%;
    box-sizing: border-box;
    justify-content: flex-start;
    text-align: left;
  }

  .season-start__submit {
    min-width: 0;
    width: 100%;
  }

  .season-start__foot-hint {
    margin-top: 12px;
  }
}

@media screen and (max-width: 380px) {
  .season-start__activity-input-wrap {
    width: 100%;
  }
}
</style>
